import React, { useCallback, useEffect, useState } from 'react';
import MainAccountBalanceChart from '../component/main_account_balance_chart';
import OpenTradesTable from '../component/OpenTradesTable';
import StatisticsOverview from '../component/StatisticsOverview';
import { ConsistencyScore } from '../component/ConsistencyScore';
import AccountGoals from '../component/AccountGoals';
import MonteCarloSimulator from '../component/MonteCarloSimulator';
import PerformanceAnalysis from '../component/PerformanceAnalysis';
import TradingCalendar from '../component/TradingCalendar';
import DayDetailModal from '../component/DayDetailModal';
import { AccountReport, LiveTradingResponse, RawTrade } from '../function/type';
import Layout from "Layout";
// import "../../Metrix/AccountLiveMetriX.scss";
// import "../../Metrix/styles.scss";
import "../../Metrix/Live/isDownload.scss";
import './Metrix.scss';
import { Link, useParams } from 'react-router-dom';
import { AccountDetailTabs, BackToOverviewButton } from 'components/Account/AccountDetailNav';
import { DownloadSimple, EyeIcon, LinkBreak, MagnifyingGlassIcon } from '@phosphor-icons/react';
import { getAccountLiveTrade, getAccountReport } from '../function/api';
import { Helmet } from 'react-helmet-async';
import { t } from 'i18next';
import { convertToLocalTime } from '../function/helper';
import { getDockerContainerStatus } from 'pages/Dashboard/BotPannel/function/api';
import { API_URL } from 'env';
import { Modal } from 'antd';

const MetrixDashboardV2: React.FC = () => {
  const { tradePlatform, id } = useParams();

  // State for countdown timer
  const [countSecond, setCountSecond] = useState(10);

  // Loading state
  const [state, setState] = useState<'loading' | 'init' | 'loaded' | 'error'>('init');
  const [liveLoadingState, setLiveLoadingState] = useState<'loading' | 'init' | 'loaded' | 'error'>('init');

  // State for fetched data
  const [accountReport, setAccountReport] = useState<AccountReport | null>(null);
  const [accountLiveTrade, setAccountLiveTrade] = useState<LiveTradingResponse | null>(null);

  // State for query parameters
  const [queryReport, setQueryReport] = useState({ dateFrom: '', dateTo: new Date().toISOString().split('T')[0] });
  const [queryLiveTrade, setQueryLiveTrade] = useState({ page: 1, limit: 100 });

  // Bot status state
  const [botStatus, setBotStatus] = useState<string>('checking');
  const [botUpdateTimer, setBotUpdateTimer] = useState<number>(30);

  const statusConfig = {
    running: {
      text: t('metrixLive.bot.running'),
      container: 'bg-emerald-50 border-emerald-200 !text-emerald-600',
      pingDot: 'animate-ping bg-emerald-400',
      dot: 'bg-emerald-500',
    },
    checking: { // Key đại diện cho trạng thái đang kiểm tra
      text: t('metrixLive.bot.checking'),
      container: 'bg-blue-50 border-blue-200 !text-blue-600',
      pingDot: 'animate-ping bg-blue-400',
      dot: 'bg-blue-500',
    },
    stopped: {
      text: t('metrixLive.bot.stopped'),
      container: 'bg-amber-50 border-amber-200 !text-amber-600',
      pingDot: 'hidden',
      dot: 'bg-amber-500',
    },
    error: {
      text: t('metrixLive.bot.error'),
      container: 'bg-rose-50 border-rose-200 !text-rose-600',
      pingDot: 'animate-ping bg-rose-400',
      dot: 'bg-rose-500',
    },
    default: {
      text: t('metrixLive.bot.notInstalled'),
      container: 'bg-slate-100 border-slate-200 !text-slate-500',
      pingDot: 'hidden',
      dot: 'bg-slate-400',
    },
  };

  const currentStatus = statusConfig[botStatus as keyof typeof statusConfig] || statusConfig.default;

  // Kết quả tài khoản lấy từ dữ liệu thật thay vì luôn "Trong quá trình"
  const accountResult = accountReport?.data?.account_info?.is_failed
    ? { text: t('metrixLive.result.failed'), color: 'bg-rose-500' }
    : accountReport?.data?.ftmo_progress?.profit_target?.is_achieved
      ? { text: t('metrixLive.result.passed'), color: 'bg-emerald-500' }
      : { text: t('metrixLive.result.inProgress'), color: 'bg-teal-500' };


  // data state

  const [sectionChartData, setSectionChartData] = useState<{
    currentBalance: number;
    equity: number;
    profitLoss: number;
    chartData: {
      labels: string[];
      datasets: number[];
    };
    chartPoints: {
      balance: number;
      targetBalance: number;
      maxloss: number;
      balanceSize: number;
    };
  }>(
    {
      currentBalance: 0,
      equity: 0,
      profitLoss: 0,
      chartData: {
        labels: [],
        datasets: [],
      },
      chartPoints: {
        balance: 0,
        targetBalance: 0,
        maxloss: 0,
        balanceSize: 0,
      }
    }
  );

  // State for personal numbers
  const [showPersonalInfo, setShowPersonalInfo] = useState(false);
  const [personalNumbers, setPersonalNumbers] = useState({
    personalYearNumber: null,
    personalMonthNumber: null,
    personalDayNumber: null
  });

  const [personalNumberMessage, setPersonalNumberMessage] = useState({
    year: '',
    month: '',
    day: ''
  });


  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalDate, setModalDate] = useState('');
  const [selectedDayTrades, setSelectedDayTrades] = useState<RawTrade[]>([]);

  const openModal = (dateStr: string, tradesOfDay: RawTrade[]) => {
    setModalDate(dateStr);
    setSelectedDayTrades(tradesOfDay);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  //--------------------------------------------Get Data--------------------------------------------

  async function fetchLiveData(accountNumber: string, page: number, limit: number) {
    try {
      const liveTrade = await getAccountLiveTrade(accountNumber, page, limit);
      setAccountLiveTrade(liveTrade || null);
      return liveTrade;
    } catch (error) {
      console.error('Error fetching live trade data:', error);
      throw new Error("Failed to fetch account live trade data");
    }
  }

  async function fetchReportData(accountNumber: string, dateFrom?: string, dateTo?: string) {
    try {
      const report = await getAccountReport(accountNumber, dateFrom, dateTo);
      setAccountReport(report || null);
      return report;
    } catch (error) {
      console.error('Error fetching report data:', error);
      throw new Error("Failed to fetch account report data");
    }
  }

  const fetchBotStatus = async () => {
    try {
      const status = await getDockerContainerStatus(tradePlatform || '', id || '');
      if (status?.status) {
        setBotStatus(status.status.State || 'unknown');
      } else {
        setBotStatus('unknown');
      }
    } catch (error) {
      setBotStatus('error');
    }
    return;
  };

  const fetchPersonalNumbers = useCallback(async () => {
    try {
      const userData = localStorage.getItem('user');
      if (!userData) return;

      const userObject = JSON.parse(userData);
      const dob = userObject.birthday;
      if (!dob) return;

      let formattedDob: string;
      const date = new Date(dob);
      if (isNaN(date.getTime())) {
        formattedDob = dob;
      } else {
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        formattedDob = `${day}/${month}/${year}`;
      }

      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No token found in localStorage');
        return;
      }
      const language = localStorage.getItem('i18nextLng') || 'en';
      const response = await fetch(`${API_URL}/api/v1/be/coaching-daily-personal?birthday=${formattedDob}&locale=${language}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();

      setPersonalNumbers({
        personalYearNumber: data.data.personal_year.number,
        personalMonthNumber: data.data.personal_month.number,
        personalDayNumber: data.data.personal_day.number
      });
      setPersonalNumberMessage({
        year: data.data.personal_year.items[0].description,
        month: data.data.personal_month.items[0].description,
        day: data.data.personal_day.items[0].description
      });
    } catch (error) {
      console.error('Error fetching personal numbers:', error);
    }
  }, []);

  // consolidated fetch function
  const fetchAllData = async (accId: string, lQuery: typeof queryLiveTrade, rQuery: typeof queryReport) => {
    setState('loading');
    setLiveLoadingState('loading');
    try {
      const [liveTradeRes, reportRes] = await Promise.all([
        fetchLiveData(accId, lQuery.page, lQuery.limit),
        fetchReportData(accId, rQuery.dateFrom, rQuery.dateTo)
      ]);

      if (liveTradeRes?.status && reportRes?.success) {
        setState('loaded');
        setLiveLoadingState('loaded');
      } else {
        setState('error');
        setLiveLoadingState('error');
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setState('error');
      setLiveLoadingState('error');
    }
  };

  // onInit
  useEffect(() => {
    if (tradePlatform && id) {
      fetchAllData(id, queryLiveTrade, queryReport);
      fetchPersonalNumbers();
    } else if (!tradePlatform || !id) {
      if (state !== 'init') {
        console.error('Missing tradePlatform or id in URL parameters');
        setState('error');
      }
    }
  }, [tradePlatform, id]);

  // Fetch data when query parameters change
  useEffect(() => {
    if (state === 'loaded' && tradePlatform && id) {
      const fetchData = async () => {
        setLiveLoadingState('loading');
        try {
          const res = await fetchLiveData(id, queryLiveTrade.page, queryLiveTrade.limit);
          if (res?.status) {
            setLiveLoadingState('loaded');
          } else {
            setLiveLoadingState('error');
          }
        } catch (error) {
          setLiveLoadingState('error');
        }
      };
      fetchData();
    }
  }, [queryLiveTrade]);

  useEffect(() => {
    if (state === 'loaded' && tradePlatform && id) {
      const fetchData = async () => {
        setState('loading');
        try {
          const res = await fetchReportData(id, queryReport.dateFrom, queryReport.dateTo);
          if (res?.success) {
            setState('loaded');
          } else {
            setState('error');
          }
        } catch (error) {
          setState('error');
        }
      };
      fetchData();
    }
  }, [queryReport]);

  // ----------------------------------------End of Get Data----------------------------------------

  //------------------------------------------Update Section Chart Data------------------------------------------

  useEffect(() => {
    if (accountReport && accountLiveTrade) {
      const { data } = accountReport;
      const liveTrade = accountLiveTrade.data.data;

      const equityCurve = data.equity_analysis?.equity_curve || [];

      const chartData = {
        labels: equityCurve.map(item => item.date ? new Date(item.date).toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit' }) : ""),
        datasets: equityCurve.map(item => Number(item.balance || 0)),
      };

      const currentBalance = Number(data.performance_summary?.current_balance || 0);
      const equity = Number(data.performance_summary?.current_equity || 0);
      // Unrealized P/L = equity − balance (includes swap/commission of open positions, like MT5).
      // Summing open positions' profit alone left out swap/commission (e.g. -136.42 vs -140.82).
      const profitLoss = currentBalance && equity
        ? Math.round((equity - currentBalance) * 100) / 100
        : liveTrade.reduce((acc, trade) => acc + Number(trade.profit || 0), 0);
      const baseBalance = Number(data.account_info?.initial_balance || (equityCurve.length > 0 ? equityCurve[0].balance : 0));
      const chartPoints = {
        balance: currentBalance,
        targetBalance: baseBalance * 1.05,
        maxloss: baseBalance * 0.9,
        balanceSize: baseBalance,
      };
      setSectionChartData({
        chartData,
        currentBalance,
        equity,
        profitLoss,
        chartPoints,
      });
    }
  }, [accountReport, accountLiveTrade]);

  useEffect(() => {
    if (countSecond === 0) {
      fetchLiveData(id || '', queryLiveTrade.page, queryLiveTrade.limit);
      setCountSecond(3)
      return
    }

    const timer = setTimeout(() => {
      setCountSecond(prev => prev - 1)
    }, 1000)

    return () => clearTimeout(timer)
  }, [countSecond])

  // Check bot status as soon as the page opens; the 30s timer below only handles refreshes
  useEffect(() => {
    fetchBotStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tradePlatform, id]);

  useEffect(() => {

    if (botUpdateTimer === 0) {
      fetchBotStatus();
      setBotUpdateTimer(30);
      return;
    }

    const timer = setTimeout(() => {
      setBotUpdateTimer(prev => prev - 1);
    }, 1000);

    return () => clearTimeout(timer);

  }, [botUpdateTimer]);


  return (
    <Layout contentClassName="page--tight">
      <Helmet>
        <title>Metrix Dashboard - {id || ''}</title>
      </Helmet>
      {
        state === 'loaded' && (
          <div className="metrix-v2-container flex flex-col antialiased ">
            {/* Main Wrapper */}
            <div className="flex-1 overflow-y-auto px-0 py-3 sm:p-6 lg:p-8"
              style={{
                scrollbarWidth: 'none',  // For Firefox
                msOverflowStyle: 'none',  // For Internet Explorer and Edge
              }}>
              <div className="max-w-[1400px] mx-auto space-y-4 sm:space-y-6 pb-12">
                {/* Title & Actions */}
                <div className="flex flex-col gap-3">
                  {/* Breadcrumb-style back link: one line on every screen size */}
                  <BackToOverviewButton className="self-start inline-flex items-center gap-1 whitespace-nowrap bg-transparent border-0 p-0 !text-sm font-medium !text-slate-500 hover:!text-brand-600 transition-colors" />
                <div className="flex flex-col xl:flex-row xl:items-center justify-between gap-3">
                  <div className="flex items-center gap-2 sm:gap-3 flex-wrap min-w-0">
                    <div className="max-sm:hidden p-2 bg-white border border-slate-200 rounded-lg shadow-sm shrink-0">
                      <svg className="w-5 h-5 !text-slate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    </div>
                    <h1 className="!text-lg sm:!text-xl font-bold !text-slate-900 flex items-center gap-2 !m-0 whitespace-nowrap">
                      {t('metrixLive.title')} <span className="font-mono-num !text-brand-600 bg-brand-50 px-2 py-0.5 rounded border border-brand-100 shadow-sm">{id}</span>
                    </h1>
                      <div className={`flex items-center gap-2 shrink-0 whitespace-nowrap !px-3 !py-1.5 rounded-full border !text-[10px] uppercase !tracking-wider font-bold shadow-sm ${currentStatus.container}`}>
                        <span className="relative flex h-2 w-2">
                          <span className={`absolute inline-flex h-full w-full rounded-full opacity-75 ${currentStatus.pingDot}`}></span>
                          <span className={`relative inline-flex rounded-full h-2 w-2 ${currentStatus.dot}`}></span>
                        </span>
                        {currentStatus.text}
                      </div>
                  </div>

                  {/* Phones: date range + icon-only PDF button share one row */}
                  <div className="flex items-center gap-2 sm:gap-3 sm:flex-wrap">
                    <div className="flex flex-1 sm:flex-none min-w-0 items-center bg-white border border-slate-200 !rounded-lg !px-2 sm:!px-3 !py-1.5 shadow-sm !text-sm transition-colors hover:border-slate-300">
                      <span className="max-sm:hidden !text-slate-400 mr-2 !text-xs font-medium uppercase tracking-wider whitespace-nowrap">{t('metrixLive.dateFilter')}:</span>
                      <input
                        type="date"
                        className="min-w-0 flex-1 sm:flex-none border-none bg-transparent outline-none !text-slate-700 font-medium font-mono-num !text-xs cursor-pointer"
                        onChange={(e) => setQueryReport(prev => ({ ...prev, dateFrom: e.target.value }))}
                        value={queryReport.dateFrom}
                      />
                      <span className="mx-1 sm:mx-2 !text-slate-300">-</span>
                      <input
                        type="date"
                        className="min-w-0 flex-1 sm:flex-none border-none bg-transparent outline-none !text-slate-700 font-medium font-mono-num !text-xs cursor-pointer"
                        onChange={(e) => setQueryReport(prev => ({ ...prev, dateTo: e.target.value }))}
                        value={queryReport.dateTo}
                      />
                    </div>
                    <Link
                      to={`/${tradePlatform}/${id}/pdf-custom`}
                      title={t('metrixLive.reportPdf')}
                      aria-label={t('metrixLive.reportPdf')}
                      className="flex shrink-0 hover:shadow-sm !no-underline items-center gap-2 px-3 sm:px-4 py-2 sm:py-1.5 whitespace-nowrap bg-white border border-slate-200 !rounded-lg shadow-sm !text-sm font-medium !text-slate-600 hover:bg-slate-50 hover:!text-brand-600 transition-colors">
                      <DownloadSimple className="w-4 h-4" />
                      <span className="max-sm:hidden">{t('metrixLive.reportPdf')}</span>
                    </Link>
                  </div>
                </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-slate-200">
                  <AccountDetailTabs platform={tradePlatform} id={id} active="detail" />
                </div>

                {/* Row 1: Charts & Account Info */}
                <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
                  <div className="xl:col-span-2 bg-white rounded-xl shadow-premium border border-slate-100 p-3 sm:p-6 flex flex-col relative overflow-hidden">
                    <div className="flex justify-between items-center mb-6 relative z-10">
                      <h2 className="!text-base font-bold !text-slate-900">{t('metrixLive.currentResult')}</h2>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 mb-6 relative z-10">
                      {[
                        { label: t('metrixLive.balance'), value: sectionChartData.currentBalance ? `$${sectionChartData.currentBalance.toLocaleString()}` : '$0.00' },
                        { label: t('metrixLive.equity'), value: sectionChartData.equity ? `$${sectionChartData.equity.toLocaleString()}` : '$0.00', isEmpty: !sectionChartData.equity, color: sectionChartData.equity < 0 ? '!text-rose-600' : '!text-emerald-600' },
                        { label: t('metrixLive.unrealizedPnl'), value: sectionChartData.profitLoss ? `${sectionChartData.profitLoss > 0 ? `$${sectionChartData.profitLoss.toLocaleString()}` : `-$${Math.abs(sectionChartData.profitLoss).toLocaleString()}`}` : t('metrixLive.noData'), isEmpty: !sectionChartData.profitLoss, color: sectionChartData.profitLoss < 0 ? '!text-rose-600' : '!text-emerald-600' },
                      ].map((kpi, idx) => (
                        <div key={idx} className="flex items-center justify-between gap-3 sm:block">
                          <div className="flex items-center gap-1 sm:mb-2">
                            <p className="!text-sm !text-slate-600 font-medium !m-0">{kpi.label}</p>
                          </div>
                          <div className="bg-slate-100/80 rounded py-2 px-3 min-w-[140px] sm:min-w-0 !text-center border border-slate-200/50">
                            {kpi.isEmpty ? (
                              <p className="!text-xs !m-0 font-medium !text-slate-400 py-1">{kpi.value}</p>
                            ) : (
                              <p className={`!text-[15px] !m-0 font-bold  font-mono-num tracking-tight ${kpi.color ? kpi.color : '!text-slate-900'}`}>{kpi.value}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="flex-1 min-h-[300px] relative w-full mt-2">
                      <div className="absolute inset-0">
                        <MainAccountBalanceChart
                          data={sectionChartData.chartData.datasets}
                          labels={sectionChartData.chartData.labels}
                          chartPoints={sectionChartData.chartPoints}
                          trades={accountReport?.data?.journal_closed_trades}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Right: Account Information */}
                  <div className="bg-white rounded-xl shadow-premium border border-slate-100 p-3 sm:p-6 flex flex-col">
                    <h2 className="!text-base font-bold !text-slate-900 mb-6">{t('metrixLive.accountInfo')}</h2>
                    <div className="flex-1 flex flex-col justify-between !text-sm">
                      {[
                        { label: t('metrixLive.result.label') + ':', value: accountResult.text, type: 'badge', color: accountResult.color },
                        { label: t('metrixLive.status') + ':', value: accountReport?.data?.account_info?.account_status || 'N/A', type: 'mono' },
                        { label: t('metrixLive.accountNumber') + ':', value: id || 'N/A', type: 'number' },
                        { label: t('metrixLive.startDate') + ':', value: convertToLocalTime(accountReport?.data?.account_info?.created_at || '', 'DD/MM/YYYY') || 'N/A', type: 'text' },
                        { label: t('metrixLive.initialBalance') + ':', value: '$' + (accountReport?.data?.account_info?.initial_balance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || accountReport?.data?.account_info?.initial_balance?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || 'N/A'), type: 'number' },
                        { label: t('metrixLive.broker') + ':', value: accountReport?.data?.account_info?.broker?.toUpperCase() || 'N/A', type: 'text' },
                        {
                          label: t('metrixLive.alignmentIndex') + ':',
                          value: (
                            <span className="flex items-center gap-1.5 cursor-pointer" >
                              DAI <strong className="font-bold text-blue-600" title={personalNumberMessage.day}>{personalNumbers?.personalDayNumber || 'N/A'}</strong>
                              • MRI <strong className="font-bold text-blue-600" title={personalNumberMessage.month}>{personalNumbers?.personalMonthNumber || 'N/A'}</strong>
                              • AMI <strong className="font-bold text-blue-600" title={personalNumberMessage.year}>{personalNumbers?.personalYearNumber || 'N/A'}</strong>
                              <EyeIcon size={16} />
                            </span>
                          ),
                          type: 'info',
                          className: 'text-xs !text-slate-400',
                          onClick: () => setShowPersonalInfo(!showPersonalInfo)
                        },
                        { label: t('metrixLive.platform', { platform: tradePlatform?.toUpperCase() || 'N/A' }) + ':', value: t('metrixLive.download'), type: 'link' },
                      ].map((item, idx) => (
                        <div key={idx}
                          onClick={item.onClick}
                          className={`flex justify-between items-center gap-3 py-2 sm:py-3 ${idx !== 7 ? 'border-b border-slate-100' : 'pt-4'}`}>
                          <span className="!text-slate-500 font-medium">{item.label}</span>
                          {item.type === 'badge' ? (
                            <span className={`px-3 py-1 rounded-full !text-xs font-bold !text-white shadow-sm ${item.color}`}>{item.value}</span>
                          ) : item.type === 'mono' ? (
                            <span className="font-bold !text-slate-900 bg-slate-100 px-2 py-0.5 rounded !text-xs uppercase tracking-wider">{item.value}</span>
                          ) : item.type === 'bot' ? (
                            <span className="flex items-center gap-1.5 px-2.5 py-1 rounded !text-xs font-bold bg-emerald-50 !text-emerald-600 border border-emerald-200 shadow-sm">
                              <span className="relative flex h-1.5 w-1.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-500"></span>
                              </span>
                              {item.value}
                            </span>
                          ) : item.type === 'number' ? (
                            <span className="font-bold !text-slate-900 font-mono-num">{item.value}</span>
                          ) : item.type === 'link' ? (
                            <Link to="/platform/download" className="font-medium !text-brand-600 hover:!text-brand-700 hover:underline">{item.value}</Link>
                          ) : (
                            <span className="font-medium !text-slate-800">{item.value}</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Open Trades */}
                <OpenTradesTable loading={liveLoadingState === 'loading'} trades={accountLiveTrade?.data.data} />

                {/* Gauge & Goals */}
                <div className="flex flex-col gap-4 sm:gap-6 md:flex-row w-full flex-grow">
                  {/* ConsistencyScore chỉ chiếm đúng chiều rộng tự nhiên, không bị méo */}
                  <div className="w-full md:w-auto flex-shrink-0">
                    <ConsistencyScore score={accountReport?.data?.consistency_score || 0} />
                  </div>

                  {/* AccountGoals sử dụng flex-1 để chiếm trọn phần không gian còn dư */}
                  <div className="flex-1 w-full min-w-0">
                    <AccountGoals report={accountReport} />
                  </div>
                </div>

                {/* Statistics Overview */}
                {
                  accountReport?.data && accountReport.data.raw_trades && accountReport.data.raw_trades.length > 0 ? (
                    <StatisticsOverview report={accountReport} />
                  ) : (
                    <div className="bg-white rounded-2xl shadow-premium border border-slate-100 p-3 sm:p-8">
                      <h2 className="!text-base font-bold !text-slate-900 mb-6">Tổng quan Thống kê Giao dịch</h2>
                      <p className="!text-slate-600 font-medium">Không có dữ liệu thống kê để hiển thị.</p>
                    </div>
                  )
                }

                {/* Trading Calendar */}
                {
                  accountReport?.data && accountReport.data.raw_trades && accountReport.data.raw_trades.length > 0 ? (
                    <TradingCalendar
                      rawTrades={accountReport.data.raw_trades}
                      onDateClick={openModal}
                    />
                  ) : (
                    <div className="bg-white rounded-2xl shadow-premium border border-slate-100 p-3 sm:p-8">
                      <h2 className="!text-base font-bold !text-slate-900 mb-6">Lịch giao dịch</h2>
                      <p className="!text-slate-600 font-medium">Không có dữ liệu giao dịch để hiển thị.</p>
                    </div>
                  )
                }
                {/* Performance Analysis & Charts */}
                <PerformanceAnalysis data={accountReport} />

                {/* Monte Carlo Simulator */}
                <MonteCarloSimulator report={accountReport} liveTrade={accountLiveTrade} />

              </div>
            </div>
            {/* Day Detail Modal */}
            <DayDetailModal
              isOpen={isModalOpen}
              onClose={closeModal}
              date={modalDate}
              trades={selectedDayTrades}
            />
            <Modal
              open={showPersonalInfo}
              title={t('BehaviourSection1Title')}
              onCancel={() => setShowPersonalInfo(false)}
              footer={null}
            >
              <div className="space-y-4 overflow-y-auto max-h-[70vh]" style={{ scrollbarWidth: "none" }}>
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">DAI</h3>
                    <span className="text-2xl font-bold text-blue-600">
                      {personalNumbers.personalDayNumber ?? "-"}
                    </span>
                  </div>
                  <p className="text-gray-600">{personalNumberMessage.day}</p>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">MRI</h3>
                    <span className="text-2xl font-bold text-blue-600">
                      {personalNumbers.personalMonthNumber ?? "-"}
                    </span>
                  </div>
                  <p className="text-gray-600">{personalNumberMessage.month}</p>
                </div>
                <div className="p-4 border border-gray-200 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-semibold">AMI</h3>
                    <span className="text-2xl font-bold text-blue-600">
                      {personalNumbers.personalYearNumber ?? "-"}
                    </span>
                  </div>
                  <p className="text-gray-600">{personalNumberMessage.year}</p>
                </div>
              </div>
            </Modal>
          </div>
        )
      }
      {
        state === 'loading' && (
          <div className="flex items-center justify-center h-[calc(100vh-64px)]">
            <div className="loader ease-linear rounded-full border-8 border-t-8 border-gray-200 h-16 w-16"></div>
          </div>
        )
      }
      {
        state === 'error' && (
          <div className="flex items-center justify-center h-[calc(100vh-64px)] flex-col gap-4">
            <LinkBreak size={32} />
            <p className="!text-slate-600 font-medium">Đã xảy ra lỗi khi tải dữ liệu. Vui lòng thử lại sau.</p>
          </div>
        )
      }
      {
        state === 'init' && (
          <div className="flex items-center justify-center h-[calc(100vh-64px)]">
            <p className="!text-slate-600 font-medium">Đang khởi tạo...</p>
          </div>
        )
      }

    </Layout>
  );
};

export default MetrixDashboardV2;
